import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import LegalDoc from './LegalDoc';
import { LEGAL_DOCS, LEGAL_VERSION } from '../constants/legalContent';
import './LegalGate.css';

// Catches accounts that predate the documents, and everyone again whenever
// LEGAL_VERSION is bumped. A signup checkbox alone would only ever cover new
// users, leaving existing customers on no agreement at all.
//
// It blocks rather than nags: the whole value of recording assent is that it
// happened before continued use, and a banner people can ignore does not give
// that. Signing out stays available so nobody is trapped.
function LegalGate({ children, onSignOut }) {
  const [status, setStatus] = useState(null);   // null = still checking
  const [reading, setReading] = useState(null); // which doc is open
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(() => {
    api.get('/legal/status')
      .then((res) => setStatus(res.data))
      // A failure here must not lock anyone out of the product - if the check
      // itself is broken, fail open and let them work.
      .catch(() => setStatus({ accepted: true, current_version: LEGAL_VERSION }));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const accept = () => {
    setSaving(true);
    setError(null);
    api.post('/legal/accept', { version: LEGAL_VERSION })
      .then((res) => setStatus(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Could not record your acceptance. Please try again.'))
      .finally(() => setSaving(false));
  };

  // Still checking, or already accepted - show the app.
  if (!status || status.accepted) return children;

  const updated = status.accepted_version && status.accepted_version !== status.current_version;

  return (
    <div className="legal-gate-overlay">
      <div className="legal-gate">
        {reading ? (
          <>
            <div className="legal-gate-readerbar">
              <button type="button" className="dash-btn cal-btn-grey" onClick={() => setReading(null)}>
                ← Back
              </button>
              <span>{reading.title}</span>
            </div>
            <div className="legal-gate-reader">
              <LegalDoc doc={reading} />
            </div>
          </>
        ) : (
          <>
            <h2 className="legal-gate-title">
              {updated ? 'We have updated our terms' : 'Before you continue'}
            </h2>
            <p className="legal-gate-lead">
              {updated
                ? `Our documents have changed since you last accepted them (you agreed to version ${status.accepted_version}; the current version is ${status.current_version}). Please review and accept to carry on.`
                : 'Please review and accept these before using Krosifin. They set out what the service does, what it does not do, and how your data is handled.'}
            </p>

            <div className="legal-gate-docs">
              {LEGAL_DOCS.map((d) => (
                <button key={d.key} type="button" className="legal-gate-doc" onClick={() => setReading(d)}>
                  <span className="legal-gate-doc-title">{d.title}</span>
                  <span className="legal-gate-doc-open">Read →</span>
                </button>
              ))}
            </div>

            <label className="legal-gate-check">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
              <span>
                I have read and agree to the Terms of Service, the Privacy Policy and the
                Disclaimer. I understand Krosifin does not file returns or make payments,
                and that dates and figures it shows must be verified before I rely on them.
              </span>
            </label>

            {error && <p className="dash-error legal-gate-error">{error}</p>}

            <div className="legal-gate-actions">
              <button type="button" className="dash-btn cal-btn-grey" onClick={onSignOut}>
                Sign out
              </button>
              <button
                type="button"
                className="dash-btn dash-btn-orange"
                disabled={!checked || saving}
                onClick={accept}
              >
                {saving ? 'Saving…' : 'Accept and continue'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LegalGate;
